const en = {
  nav: {
    loggedOutToast: "You have been logged out.",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    platformSectionLabel: "Platform",
    expandSidebar: "Expand sidebar",
    collapseSidebar: "Collapse sidebar",
    collapse: "Collapse",
    resizeSidebar: "Resize sidebar",
    dashboard: "Dashboard",
    customers: "Customers",
    suppliers: "Suppliers",
    sales: "Sales",
    expenses: "Expenses",
    catalog: "Catalog",
    inventory: "Inventory",
    employees: "Employees",
    businessSettings: "Business Settings",
    platformAdmin: "Platform Admin",
    accountSettings: "Account",
    logout: "Log out",
    calendar: "Calendar",
    goals: "Goals",
    notifications: "Notifications",
  },
  auth: {
    brand: "Adam",
    layout: {
      tagline: "Your sales, customers, inventory and goals — one place, live.",
      backHome: "Back to home",
    },
    password: {
      show: "Show password",
      hide: "Hide password",
    },
    login: {
      subtitle: "Sign in to your account",
      email: "Email",
      emailPlaceholder: "you@example.com",
      password: "Password",
      passwordPlaceholder: "Enter your password",
      submit: "Sign in",
      submitting: "Signing in…",
      forgotLink: "Forgot your password?",
      signupPrompt: "Don't have an account?",
      signupLink: "Sign up",
      welcomeToast: "Welcome back!",
      genericError: "Login failed. Please try again.",
      invalidCredentials: "Incorrect email or password.",
      notVerified: "Your account isn't verified yet. Check your email for the verification link.",
      tooManyAttempts: "Too many attempts. Please wait a minute and try again.",
      resendVerification: "Resend verification email",
      verificationSent: "Verification email sent. Check your inbox.",
    },
    register: {
      subtitle: "Create your account",
      firstName: "First name",
      lastName: "Last name",
      email: "Email",
      emailPlaceholder: "you@example.com",
      password: "Password",
      passwordPlaceholder: "Min. 8 characters",
      confirmPassword: "Confirm password",
      confirmPlaceholder: "Repeat your password",
      submit: "Create account",
      submitting: "Creating account…",
      loginPrompt: "Already have an account?",
      loginLink: "Sign in",
      inviteHint: "Invited to a team? Open the link in your invitation email to join.",
      successTitle: "Check your email",
      successWhy: "We sent a verification link to keep your account secure.",
      successWhen: "It usually arrives within a minute. If you don't see it, check your spam folder.",
      successExpiry: "The verification link expires in 3 days.",
      emailSendFailed:
        "We couldn't send the email just now. Use the resend button below in a moment — your account was created.",
      successResend: "Resend verification email",
      successResending: "Sending…",
      successResendSent: "Sent! Check your inbox again.",
      successBackLink: "Back to sign in",
      successToast: "Account created! Check your email to verify.",
      genericError: "Registration failed.",
      validation: {
        emailRequired: "Email is required.",
        firstNameRequired: "First name is required.",
        lastNameRequired: "Last name is required.",
        passwordTooShort: "Password must be at least 8 characters.",
        passwordsDoNotMatch: "Passwords do not match.",
        emailExists: "An account with this email already exists. Try signing in or resetting your password.",
      },
    },
    forgot: {
      subtitle: "Reset your password",
      email: "Email",
      emailPlaceholder: "you@example.com",
      submit: "Send reset link",
      submitting: "Sending…",
      backLink: "Back to sign in",
      successTitle: "Check your email",
      successBody: "If an account exists for {{email}}, we sent a password reset link.",
      successExpiry: "The reset link expires in 3 days.",
    },
    reset: {
      subtitle: "Choose a new password",
      newPassword: "New password",
      passwordPlaceholder: "Min. 8 characters",
      passwordTooShort: "Password must be at least 8 characters.",
      submit: "Reset password",
      submitting: "Resetting…",
      invalidTitle: "Invalid reset link",
      invalidBody: "This password reset link is invalid. Please request a new one.",
      requestNewLink: "Request new link",
      failedExpired:
        "Reset failed. The link may have expired (links last 3 days) or the password was too weak — request a new link and try a stronger password.",
      failedGeneric: "Reset failed. Please try again.",
      successTitle: "Password reset!",
      successBody: "Your password has been updated. You can now sign in.",
      signinLink: "Sign in",
    },
    verify: {
      loading: "Verifying your email…",
      successTitle: "Email verified!",
      signinLink: "Sign in",
      errorTitle: "Verification failed",
      errorBodyMissing: "This link is missing some pieces. Open the email again and click directly on the verification button.",
      errorBodyExpired: "This verification link is invalid or has expired. Use the resend button below or register again.",
      registerLink: "Register again",
      resendButton: "Resend verification email",
      resending: "Sending…",
      resendSent: "Sent! Check your inbox again.",
      resendCooldown: "You can request another in {{seconds}}s",
      resendPrompt: "Need a new link? Enter your email:",
    },
    joinBusiness: {
      loadingInvite: "Loading your invitation…",
      invalidTitle: "Invalid or expired invitation",
      invalidBody:
        "This invitation link is no longer valid. Ask your administrator to send a new one.",
      loginLink: "Go to login",
      title: "Join {{business}}",
      invitedBy: "{{inviter}} invited you to join as {{role}}.",
      invitedByNoName: "You've been invited to join as {{role}}.",
      emailLocked: "This is the address your invitation was sent to.",
      submit: "Create account & join",
      submitting: "Joining…",
      existingBody:
        "An account already exists for {{email}}. Accept the invitation to join {{business}}.",
      acceptCta: "Accept invitation",
      accepting: "Joining…",
      successTitle: "Check your email",
      successBody:
        "We sent a verification link to {{email}}. Verify it, then log in to get started.",
      acceptedTitle: "You're in!",
      gotoLogin: "Log in",
      genericError: "Something went wrong. Please try again.",
      verifyFirstTitle: "Verify your email first",
      verifyFirstBody:
        "An account for {{email}} exists but hasn't been verified. Verify it from the email we sent you, then log in — you're already set up to join {{business}}.",
      resendVerification: "Resend verification email",
      alreadyMember:
        "This account already belongs to another business. Each account can be a member of only one business.",
      expired: "This invitation has expired. Ask your administrator to send a new one.",
    },
  },
  help: {
    title: "Help",
    gettingStarted: "Getting started",
    gettingStartedHint: "Reopen the setup checklist",
    documentation: "Documentation",
    documentationHint: "How Adam works",
    checklistModal: {
      title: "Getting started",
      subtitle: "Complete these to finish setting up your workspace.",
      allDone: "You're all set — every step is complete. 🎉",
      close: "Close",
    },
    doc: {
      title: "Documentation",
      subtitle: "How Adam works, module by module.",
      groups: {
        start: "Get started",
        modules: "Modules",
      },
      onThisPage: "On this page",
    },
  },
  common: {
    avatarAlt: "Avatar",
    uploadImage: "Upload image",
    dismiss: "Dismiss",
    save: "Save",
    create: "Create",
    cancel: "Cancel",
    close: "Close",
    delete: "Delete",
    edit: "Edit",
    search: "Search",
    loading: "Loading",
    actions: "Actions",
    page: "Page",
    of: "of",
    previous: "Previous",
    next: "Next",
    noResults: "No results",
    total: "Total",
    exportCsv: "Export CSV",
    addedBy: "Added by",
    thisMonth: "This month",
    saving: "Saving...",
    confirm: "Confirm",
    createdBy: "Created by",
    updatedBy: "Modified by",
    deleting: "Deleting…",
    pageOf: "Page {{page}} of {{total}}",
    view: "View",
    none: "None",
    selectOption: "Select…",
  },
  payStatusActions: {
    cancel: "Cancel",
    makePayment: "Make payment",
    markAsPaid: "Mark as paid",
  },
  status: {
    draft: "Draft",
    confirmed: "Confirmed",
    cancelled: "Cancelled",
    recorded: "Recorded",
    approved: "Approved",
    rejected: "Rejected",
    active: "Active",
    inactive: "Inactive",
    unpaid: "Unpaid",
    partial: "Partial",
    paid: "Paid",
  },
  dashboard: {
    agingDays: "days",
    heatmapDow: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    incomeUncategorized: "Uncategorized",
    vsLabel: "vs",
    minAbbrev: "min",
    title: "Dashboard",
    totalCustomers: "Total Customers",
    confirmedRevenue: "Confirmed Revenue",
    confirmedSalesCount: "Confirmed Sales Count",
    newCustomers: "New Customers",
    confirmedSales: "Confirmed sales",
    confirmedCount: "Confirmed",
    newThisMonth: "+{{count}} this month",
    revenueChart: "Revenue vs. Expenses",
    topCustomers: "Top Customers",
    topCustomersSub: "By confirmed revenue in period",
    customer: "Customer",
    totalSpent: "Total Spent",
    noChartData: "No data yet for this period.",
    legendRevenue: "Revenue",
    legendExpenses: "Expenses",
    last7days: "7d",
    last30days: "30d",
    last90days: "90d",
    last365days: "1yr",
    customRange: "Custom",
    today: "Today",
    yesterday: "Yesterday",
    last7daysLong: "Last 7 days",
    last30daysLong: "Last 30 days",
    last90daysLong: "Last 90 days",
    lastYear: "Last year",
    fullHistory: "Full history",
    thisMonth: "This month",
    lastMonth: "Last month",
    dateRangeApply: "Apply",
    dateRangeClear: "Clear",
    dateRange: "Date range",
    dateRangeStart: "Start",
    dateRangeEnd: "End",
    cumWithin: "Within range",
    cumAllTime: "All time",
    cumLegend: "Range",
    viewModeLegend: "View",
    granularityLegend: "Granularity",
    kpisTitle: "Summary",
    trendsTitle: "Trends",
    newInPeriod: "+{{count}} in period",
    expensesApproved: "Approved Expenses",
    expensesPending: "Pending Expenses",
    expensesApprovedSub: "Approved last 30 days",
    expensesPendingSub: "Awaiting approval",
    catalogActive: "Active Catalog Items",
    catalogValue: "Catalog Value",
    catalogActiveSub: "Active products & services",
    catalogValueSub: "Sum of active prices",
    employeesActive: "Active Team Members",
    employeesActiveSub: "Active in your team",
    analyticsTitle: "Analytics",
    forecastTitle: "Forecasting",
    cumulativeChart: "Cumulative Revenue vs. Expenses",
    unifiedTrendChart: "Revenue & expenses",
    viewModePeriod: "Period",
    viewModeCumulative: "Cumulative",
    salesByCategoryChart: "Sales by Category",
    expenseBreakdownChart: "Expense Breakdown",
    customerGrowthChart: "Customer Growth",
    avgSaleValueChart: "Average Sale Value",
    granularityDaily: "Daily",
    granularityWeekly: "Weekly",
    granularityMonthly: "Monthly",
    infoTitle: "How to read this",
    infoButtonLabel: "How to interpret",
    chartNoData: "No data in this window.",
    series: {
      revenue: "Revenue",
      expense: "Expense",
      net: "Net",
      cumRevenue: "Cumulative revenue",
      cumExpense: "Cumulative expense",
      cumNet: "Cumulative net",
      rollingMargin: "Rolling margin %",
      cumulative: "Cumulative",
      newCustomers: "New customers",
      newSuppliers: "New suppliers",
      repeatCustomers: "Repeat customers",
      margin: "Margin %",
      recurring: "Recurring",
      oneoff: "One-off",
      completed: "Completed",
      overdue: "Overdue",
      count: "Count",
      total: "Total",
      units: "Units",
      cumShare: "Cumulative share",
      avgDays: "Average days",
      conversionRate: "Conversion rate",
      confirmed: "Confirmed",
      paid: "Paid",
    },
    revenueTrendChart: "Revenue trend",
    revenueShareChart: "Revenue share over time",
    basketSize: "Basket size",
    salesHeatmap: "Sales heatmap",
    receivablesAging: "Receivables aging",
    payablesAging: "Payables aging",
    salesBreakdown: "Sales breakdown",
    expenseTrendChart: "Expense trend",
    expenseShareChart: "Expense share over time",
    expenseHeatmap: "Expense activity heatmap",
    avgExpenseChart: "Average Expense Value",
    topSuppliers: "Top suppliers",
    upcomingRecurring: "Upcoming recurring",
    expenseBreakdown: {
      recurrency: "Recurrency",
      category: "Category",
      supplier: "Supplier",
    },
    paretoChart: "Pareto curve · revenue by product",
    stockBreakdownChart: "Stock value breakdown",
    stockBreakdown: {
      product: "Product",
      supplier: "Supplier",
    },
    stockStrip: {
      outOfStock: "Out of stock",
      belowRop: "Low stock",
      healthy: "Healthy",
    },
    lowStockItems: "Low-stock items",
    lowStockEmpty: "Every tracked item is above its minimum threshold.",
    daysOfCover: "Days of cover",
    turnoverByProduct: "Turnover by product",
    daysOfCoverItems: "Items in this category",
    moreItems: "+{{count}} more",
    coverBuckets: {
      noSales: "No sales",
    },
    customerTypeSplit: "Customer type split",
    ltvDistribution: "Lifetime value distribution",
    churnProxy: "Churn proxy",
    cohortRetention: "Cohort retention",
    topByGeography: "Top by geography",
    topCustomersByLtv: "Top customers by LTV",
    customerGrowthCumulative: "Cumulative customer growth",
    cumulativeRevenue: "Cumulative revenue",
    cumulativeExpenses: "Cumulative expenses",
    customerHealth: {
      active: "Active",
      at_risk: "At risk",
      churned: "Churned",
    },
    col: {
      city: "City",
      customers: "Customers",
      orders: "Orders",
      ltv: "Lifetime value",
      expenses: "Expenses",
      totalSpend: "Total spend",
      openExpenses: "Open expenses",
      balanceDue: "Balance due",
    },
    supplierGrowth: "New suppliers over time",
    spendMixChart: "Spend mix",
    spendMix: {
      category: "Category",
      supplier: "Supplier",
    },
    concentrationChart: "Concentration · share of spend",
    paymentDelayChart: "Avg payment delay",
    openBalancesChart: "Suppliers with open balances",
    topSuppliersLifetime: "Top suppliers · lifetime spend",
    paymentDelayStrip: {
      onTime: "On-time",
      late: "Late",
      veryLate: "Very late",
    },
    noOpenBalances: "No outstanding payable balances.",
    turnoverByCategory: "Turnover by category",
    topProducts: "Top products",
    "salesBreakdown.product": "Product",
    "salesBreakdown.category": "Category",
    "salesBreakdown.customer": "Customer",
    "salesBreakdown.other": "Other",
    payStrip: {
      unpaidCount: "Unpaid sales",
      partialCount: "Partial sales",
      paidCount: "Paid sales",
      unpaidExpensesCount: "Unpaid expenses",
      partialExpensesCount: "Partial expenses",
      paidExpensesCount: "Paid expenses",
    },
    heatmap: {
      count: "Sales",
      revenue: "Revenue",
      partNight: "Night",
      partMorning: "Morning",
      partAfternoon: "Afternoon",
      partEvening: "Evening",
      openFullGrid: "Open full grid",
    },
    info: {
      cashFlow:
        "Each period stacks revenue (in) and expense (out) plus a net line above/below zero. A widening gap signals improving margins; a net line dipping below zero is a cash-burn warning.",
      cumulative:
        "Running totals from the start of your selected window. Steeper slopes mean faster accumulation; a flattening curve signals a slowdown.",
      pieMix:
        "Each slice's area is its share of the total. Use it to spot dominant categories and to sanity-check that no single bucket should be split or grouped further.",
      heatmap:
        "Each cell's color intensity is the relative volume for that day-of-week and hour-of-day. Use it to spot peak windows and dead zones.",
      avgSale:
        "Average ticket size per period. Rising values usually mean better cross-sell or higher-margin product mix; falling values may signal discounting or a shift toward smaller orders.",
      revenueTrend:
        "Total confirmed revenue per period. Compare slopes across granularities to separate noise from real trend.",
      expenseTrend:
        "Total approved expenses per period. Watch for spikes that don't align with revenue growth.",
      revenueByCategory:
        "Revenue split by product/service category. Helps identify mix shifts and which lines drive the top line.",
      salesBreakdown:
        "Total revenue grouped by product, category, or customer. Top 10 entries with the rest rolled into 'Other'.",
      revenueShare:
        "Per-period revenue stacked by the top 5 entries along the chosen dimension. The 'Other' band rolls up the long tail so the legend stays scannable.",
      expenseShare:
        "Per-period expense stacked by recurrency, category, or supplier. Includes catch-all buckets so every dimension sums to the same period total.",
      expenseByCategory:
        "Approved spend split by category. Categories near the top deserve the most scrutiny.",
      basket:
        "Histogram of individual sale amounts. A long right tail means a few large orders carry the period; a tight distribution means consistent ticket sizes.",
      newVsRepeat:
        "Per-period revenue split by customer type. A healthy mix has a stable repeat base growing alongside new acquisition.",
      profitMargin:
        "Profit margin = (revenue − expense) ÷ revenue, per period. A trend line above ~10% is usually viable; sustained dips below zero signal structural cost issues.",
      unifiedTrend:
        "Per-period revenue, expense, and net on the left axis with the profit-margin % on the right. Switch to Cumulative for running totals; the margin line then tracks the rolling margin (cumulative net ÷ cumulative revenue).",
      categoryShare:
        "Stacked spend by category over time. Rising bands mean a category is growing in share; thin bands are stable or shrinking.",
      aging:
        "Open balances grouped by how long they've been outstanding. Concentration in the older buckets signals collection or payment risk.",
      topSuppliers:
        "Suppliers ranked by approved spend. Use it to gauge concentration and start vendor consolidation conversations.",
      topCustomers:
        "Customers ranked by confirmed revenue in the window. Pareto usually applies — a small set drives the majority.",
      pareto:
        "Cumulative revenue share by descending product. The 80% line is the classic Pareto cut: items to its left typically warrant the most attention.",
      daysOfCover:
        "How long current stock lasts at the recent sales rate: current stock ÷ average units sold per day in the selected window. Items are grouped by that runway — under 7 days needs reordering now; over 90 days is overstock; 'no-sales' means nothing sold in the window. Bars run red (urgent) → blue (overstocked).",
      turnoverByCategory:
        "Units sold ÷ current units in stock, per category. Higher = faster sell-through; near zero = slow movers.",
      cohort:
        "Each row is a cohort acquired in that month; columns show what fraction returned in subsequent months. Healthy products show a stabilizing tail well above zero.",
      typeSplit:
        "Customer count split by type. Mostly used as a context lens for the other charts.",
      geography:
        "Where your customers live. Use it to evaluate market depth and where to focus growth.",
      ltvDistribution:
        "Histogram of total spend per customer. A long right tail tells you how concentrated lifetime value is in a few accounts.",
      churn:
        "Customers grouped by days since their last purchase. The right-hand buckets are your at-risk and likely-churned segments.",
      supplierGrowth:
        "New suppliers added per period. Spikes often signal sourcing changes; flat means stable vendor base.",
      categorySplitSuppliers:
        "Spend split by expense category for vendors. Reveals where supplier dollars actually go.",
      concentrationHHI:
        "Herfindahl-Hirschman Index of vendor spend concentration (0–10000). <1500 competitive, 1500–2500 moderate, >2500 high — high values mean over-reliance on a few suppliers.",
      paymentDelay:
        "Average days from expense date to last payment, per supplier. Long delays may strain relationships and miss early-pay discounts.",
      revenuePerEmployee:
        "Confirmed revenue attributed to each team member. Pair with conversion to read effort vs outcome.",
      expensePerEmployee:
        "Approved expense recorded by each team member. Outliers worth checking against policy.",
      eventsPerEmployee:
        "Calendar events completed vs overdue per team member. Use it to spot capacity issues, not as a productivity score in isolation.",
      teamActivity:
        "Per-period count of confirmed sales (count) and total revenue (currency). Use it to see whether more activity is translating into more revenue.",
      netContribution:
        "Revenue attributed minus expense recorded, per team member. A first-pass financial scoreboard — refine with role context before drawing conclusions.",
      conversion:
        "Completed events divided by sales count, per team member. Higher rates mean more output per booked activity.",
      revenueVsPrior:
        "Current-window revenue compared to the same-length window immediately before it. Useful to read momentum without seasonality bias.",
    },
    tabs: {
      overview: "Overview",
      money: "Money",
      operations: "Operations",
      team: "Team",
      sales: "Sales",
      expenses: "Expenses",
      finance: "Finance",
      inventory: "Inventory",
      customers: "Customers",
      suppliers: "Suppliers",
      analyticsHub: "Analytics Hub",
    },
    analyticsHub: {
      title: "Analytics Hub",
      backToHub: "Back to Analytics Hub",
      cards: {
        sales: { title: "Sales", description: "Revenue, orders, and payment health." },
        expenses: { title: "Expenses", description: "Cost trend, recurring vs one-off, and the contribution waterfall." },
        finance: { title: "Finance", description: "Cash flow, AR/AP aging, margin trend, and supplier spend mix." },
        inventory: { title: "Inventory", description: "Stock value, turnover, Pareto curve, and items below ROP." },
        customers: { title: "Customers", description: "Growth, retention cohorts, RFM, LTV, and activity heatmap." },
        suppliers: { title: "Suppliers", description: "Spend mix, concentration, payment delay, and open balances." },
      },
    },
    trendsGroup: "Trends over time",
    trendsGroupSub: "Every chart here follows the selected granularity.",
    typeUnspecified: "Not defined",
    churnNever: "Never",
    recurringSplitChart: "Recurring vs one-off",
    spendByCategory: "Spend by category",
    spendComposition: "Spend composition",
    assigned: "Assigned",
    unassigned: "Unassigned",
    compose: {
      byCategory: "By category",
      assignedVsUnassigned: "Assigned vs Unassigned",
      bySupplier: "By supplier",
    },
    team: {
      activityTrend: "Team activity trend",
      netContribution: "Net contribution per employee",
      revenuePerEmployee: "Revenue per employee",
      expensesPerEmployee: "Expenses per employee",
      eventsCompletedOverdue: "Events: completed vs overdue",
      conversion: "Sales-to-event conversion",
      recentActivity: "Recent activity",
      completedEvents: "Completed events",
      overdueEvents: "Overdue events",
      col: {
        event: "Event",
        actor: "Actor",
        when: "When",
      },
    },
    subtitles: {
      salesBreakdown: "Top contributors for the selected dimension.",
      heatmap: "When sales happen, by weekday.",
      basket: "Distribution of order totals.",
      typeSplit: "Mix of customer types.",
      ltvDistribution: "How customers spread across lifetime-value bands.",
      churn: "Customers grouped by days since last purchase.",
      cohort: "Repeat-purchase retention by signup month.",
      topCustomersByLtv: "Highest lifetime-value customers.",
    },
    kpis: {
      revenue: "Revenue",
      expense: "Expenses",
      net: "Net",
      margin: "Margin %",
      marginLabel: "margin",
      marginTrend: "Margin trend",
      vsPrior: "vs prior period",
      equationLess: "minus",
      equationEquals: "equals",
      netStoryEquation: "Revenue minus expenses",
      netStoryKept: "You kept {{pct}}% of revenue",
      netStoryLoss: "Expenses outpaced revenue",
      pendingIn: "Pending in",
      pendingOut: "Pending out",
      pendingNone: "All settled",
      netCompareThisPeriod: "This period",
      netComparePriorPeriod: "Prior period",
      netCompareAria: "Net comparison: this period {{current}}, prior period {{previous}}",
      salesCount: "Sales count",
      avgOrderValue: "Avg order value",
      paidPct: "Paid %",
      outstanding: "Outstanding",
      total: "Total",
      count: "Count",
      avg: "Average",
      headcount: "Headcount",
      expensesTotal: "Expenses",
      expenseCount: "Expense count",
      avgExpenseValue: "Avg expense value",
      recurringShare: "Recurring share",
      netCashFlow: "Net cash flow",
      arBalance: "AR balance",
      apBalance: "AP balance",
      stockValue: "Stock value",
      turnoverRate: "Turnover rate",
      lowStock: "Low-stock items",
      belowRop: "Items below ROP",
      totalSkus: "Total SKUs",
      deadStock: "Dead-stock value",
      revenuePareto: "80% of revenue",
      spendPareto: "80% of spend",
      paretoSubCustomers: "of {{count}} customers",
      paretoSubSuppliers: "of {{count}} suppliers",
      totalCustomers: "Total customers",
      newInPeriod: "New in period",
      atRisk: "At-risk",
      avgLtv: "Avg LTV",
      totalSuppliers: "Total suppliers",
      activeSuppliers90d: "Active 90d",
      openBalance: "Open balance",
      hhi: "HHI concentration",
    },
    waterfall: {
      title: "Contribution waterfall",
      info: "How the period total breaks down by the chosen dimension.",
      total: "Total",
      other: "Other",
      noData: "No contribution data in this range.",
      bySales: {
        legend: "Group by",
        product: "Product",
        category: "Category",
        customer: "Customer",
      },
      byExpenses: {
        legend: "Group by",
        recurrency: "Recurrency",
        category: "Category",
        supplier: "Supplier",
        recurring: "Recurring",
        oneoff: "One-off",
      },
    },
    overviewSections: {
      topSuppliersMini: "Top suppliers",
      incomeFlow: "Income statement flow",
      incomeFlowInfo:
        "Sankey-style P&L: revenue sources flow into Net revenue, which splits into Net profit and Operating expenses by category.",
    },
    incomeFlow: {
      netRevenue: "Net revenue",
      netProfit: "Net profit",
      operatingExpenses: "Operating expenses",
      sourcesLegend: "Sources",
      sinksLegend: "Sinks",
      stageSources: "Sources",
      stageNetRevenue: "Net revenue",
      stageSplit: "Split",
      stageDestinations: "Outflows",
    },
    alerts: {
      title: "Alerts",
      lowStock: "Low-stock items",
      overdueAr: "Overdue receivable sales",
      overdueAp: "Overdue payable expenses",
      outstanding: "outstanding",
      andMore: "+{{count}} more",
      lowStockAria: "{{count}} items are below their minimum stock level",
      overdueArAria: "{{count}} sales are overdue and unpaid",
      overdueApAria: "{{count}} expenses are overdue and unpaid",
    },
    goals: {
      activeTitle: "Active goals",
      viewAll: "View all",
      ringAria: "{{name}} is at {{pct}} percent of target",
      prev: "Previous goals",
      next: "Next goals",
    },
    cohortTable: {
      cohort: "Cohort",
      size: "Size",
      month1: "Month 1",
      month3: "Month 3",
      month6: "Month 6",
      month12: "Month 12",
      empty: "Not enough history to build cohorts yet.",
    },
    empty: {
      title: "Let's set up your business",
      subtitle: "Pick a starting point — you can do these in any order, and your dashboard will fill in as you go.",
      addCustomerTitle: "Add your first customer",
      addCustomerBody: "Save who you sell to so you can build history and run reports.",
      addProductTitle: "Add a product or service",
      addProductBody: "Add what you sell — products track inventory automatically.",
      recordSaleTitle: "Record your first sale",
      recordSaleBody: "Even one sale gets the dashboard moving and proves the flow.",
    },
  },
  customers: {
    title: "Customers",
    countTotal: "{{count}} total",
    add: "Add Customer",
    searchPlaceholder: "Search by name, email, or phone…",
    allActivity: "All activity",
    name: "Name",
    email: "Email",
    phone: "Phone",
    address: "Address",
    notes: "Notes",
    noCustomers: "No customers yet. Add your first one.",
    deleteTitle: "Delete Customer",
    deleteMessage: "Delete {{name}}? This cannot be undone.",
    created: "Customer created.",
    updated: "Customer updated.",
    deleted: "Customer deleted.",
    failedCreate: "Failed to create customer.",
    newCustomer: "New Customer",
    editCustomer: "Edit Customer",
    createCustomer: "Create Customer",
    saveChanges: "Save Changes",
    back: "Customers",
    totalCustomers: "Total Customers",
    newThisMonth: "New This Month",
    withEmail: "Have Email",
    withPhone: "Have Phone",
    repeat: "Repeat",
    topRevenue80pct: "80% of Revenue",
    topRevenue80pctSub: "Fewest customers explaining 80% of revenue",
    fields: {
      name: "Name",
      email: "Email",
      phone: "Phone",
      address: "Address",
      notes: "Notes",
      date_of_birth: "Date of Birth",
      city: "City",
      state: "State / Province",
      country: "Country",
      customer_type: "Customer Type",
    },
    customerType: {
      individual: "Individual",
      company: "Company",
    },
    salesHistory: "Sales History",
    contactInfo: "Contact Info",
    backToCustomers: "Back to Customers",
    notFound: "Customer not found.",
    noNotes: "No notes.",
    noSales: "No sales yet for this customer.",
    deleteMessageWithSales: "Delete {{name}}? All associated sales will lose their customer link.",
    kpis: {
      lifetimeValue: "Lifetime Value",
      lifetimeValueSub: "Confirmed sales",
      lastPurchase: "Last Purchase",
      lastPurchaseSubNone: "No purchases yet",
      outstandingBalance: "Outstanding Balance",
      outstandingBalanceSubAllPaid: "All paid up",
      outstandingBalanceSubOwed: "Owed across confirmed sales",
    },
  },
  suppliers: {
    title: "Suppliers",
    countTotal: "{{count}} total",
    add: "Add Supplier",
    searchPlaceholder: "Search by name, email, or contact…",
    name: "Name",
    email: "Email",
    phone: "Phone",
    address: "Address",
    contactPerson: "Contact Person",
    contactInfo: "Contact Info",
    notes: "Notes",
    noNotes: "No notes.",
    noSuppliers: "No suppliers yet. Add your first one.",
    noExpenses: "No expenses linked to this supplier yet.",
    deleteTitle: "Delete Supplier",
    deleteMessage: "Delete {{name}}? This cannot be undone.",
    created: "Supplier created.",
    updated: "Supplier updated.",
    deleted: "Supplier deleted.",
    failedCreate: "Failed to create supplier.",
    newSupplier: "New Supplier",
    editSupplier: "Edit Supplier",
    createSupplier: "Create Supplier",
    saveChanges: "Save Changes",
    back: "Suppliers",
    backToSuppliers: "Back to Suppliers",
    notFound: "Supplier not found.",
    totalSuppliers: "Total Suppliers",
    newThisMonth: "New This Month",
    withEmail: "Have Email",
    withPhone: "Have Phone",
    active90d: "Active 90d",
    active90dSub: "With activity in the last 90 days",
    openBalance: "Open Balance",
    openBalanceSub: "Owed across supplier expenses",
    topSpend80pct: "80% of Spend",
    topSpend80pctSub: "Fewest suppliers explaining 80% of spend",
    expenseHistory: "Expense History",
    kpis: {
      lifetimeValue: "Lifetime Spend",
      lifetimeValueSub: "All-time expenses",
      lastPurchase: "Last Purchase",
      lastPurchaseSubNone: "No expenses yet",
      outstandingBalance: "Outstanding Balance",
      outstandingBalanceSubAllPaid: "All paid up",
      outstandingBalanceSubOwed: "Owed across expenses",
    },
  },
  sales: {
    title: "Sales",
    countTotal: "{{count}} total",
    new: "New Sale",
    confirmedCount: "Confirmed this period",
    searchPlaceholder: "Search sales…",
    allStatuses: "All statuses",
    allPayStatuses: "All pay statuses",
    noSales: "No sales yet.",
    deleteTitle: "Delete Sale",
    deleteMessage: "Delete this sale? This cannot be undone.",
    titleField: "Title",
    idField: "Sale ID",
    customer: "Customer",
    noCustomer: "No customer",
    walkIn: "Walk-in",
    status: "Status",
    date: "Sale Date",
    total: "Total",
    items: "Items",
    notes: "Notes",
    created: "Sale created.",
    deleted: "Sale deleted.",
    updated: "Sale updated.",
    totalRevenue: "Total Revenue",
    confirmedSales: "Confirmed Sales",
    avgSaleValue: "Avg. Sale Value",
    confirmedRevenue: "Confirmed revenue",
    avgValue: "Average value",
    outstanding: "Outstanding",
    outstandingSub: "Owed across confirmed sales",
    newSale: "New Sale",
    editSale: "Edit Sale",
    createSale: "Create Sale",
    saveChanges: "Save Changes",
    addItem: "+ Add item",
    description: "Product / service",
    quantity: "Qty",
    unitPrice: "Unit $",
    grandTotal: "Total",
    back: "Sales",
    itemDescription: "Product / service",
    qty: "Qty",
    inventoryStockOnConfirm: "Stock will be decremented when this sale is confirmed.",
    payStatus: "Pay Status",
    markedPaid: "Sale marked as paid.",
    paymentsCancelled: "Sale payments cancelled.",
    cancelPaymentsTitle: "Cancel sale payments?",
    cancelPaymentsMessage: "All payments on this sale will be removed and the sale will revert to unpaid. This cannot be undone.",
    cancelPaymentsConfirm: "Cancel payments",
    exportReceipt: "Export receipt",
    receiptExportFailed: "Failed to export receipt.",
  },
  expenses: {
    title: "Expenses",
    countTotal: "{{count}} total",
    new: "New Expense",
    searchPlaceholder: "Search expenses…",
    allCategories: "All categories",
    allPayStatuses: "All pay statuses",
    noExpenses: "No expenses yet.",
    deleteTitle: "Delete Expense",
    deleteMessage: "Delete this expense? This cannot be undone.",
    titleField: "Title",
    idField: "Expense ID",
    amount: "Amount",
    category: "Category",
    date: "Expense Date",
    notes: "Notes",
    created: "Expense created.",
    deleted: "Expense deleted.",
    updated: "Expense updated.",
    totalRecorded: "Total Recorded",
    recordedCount: "Recorded Count",
    recordedExpenses: "Recorded expenses",
    recordedThisPeriod: "Recorded this period",
    topCategory: "Top Category",
    avgExpense: "Avg. Expense",
    avgExpenseSub: "Average per recorded expense",
    outstanding: "Outstanding",
    outstandingSub: "Owed across expenses",
    newExpense: "New Expense",
    editExpense: "Edit Expense",
    createExpense: "Create Expense",
    saveChanges: "Save Changes",
    back: "Expenses",
    catOffice: "Office",
    catTravel: "Travel",
    catMeals: "Meals",
    catSoftware: "Software",
    catEquipment: "Equipment",
    catMarketing: "Marketing",
    catOther: "Other",
    inventoryStockOnCreate: "Stock will be added when this expense is recorded.",
    payStatus: "Pay Status",
    markedPaid: "Expense marked as paid.",
    paymentsCancelled: "Expense payments cancelled.",
    cancelPaymentsTitle: "Cancel expense payments?",
    cancelPaymentsMessage: "All payments on this expense will be removed and the expense will revert to unpaid. This cannot be undone.",
    cancelPaymentsConfirm: "Cancel payments",
    recurringSection: "Recurrence",
    isRecurring: "Make this a recurring expense",
    recurrenceFrequency: "Frequency",
    recurrenceEvery: "Every",
    recurrenceInterval: "Interval",
    recurrencePeriod: "Period",
    periodDays: "Days",
    periodWeeks: "Weeks",
    periodMonths: "Months",
    periodYears: "Years",
    recurrenceEndType: "Ends",
    recurrenceEndCount: "After how many occurrences?",
    recurrenceEndDate: "End date",
    recurringBadge: "Recurring",
    endNever: "Never",
    endCount: "After N occurrences",
    endDate: "On a specific date",
    supplier: "Supplier",
    noSupplier: "No supplier",
    receipt: "Receipt",
    receiptAttach: "Attach receipt",
    receiptReplace: "Replace receipt",
    receiptOpen: "Open",
    receiptHint: "JPEG, PNG, GIF, WEBP, HEIC, or PDF",
    receiptWillUploadOnSave: "{{name}} will upload when you save.",
    receiptUploaded: "Receipt uploaded.",
    receiptError: "Couldn't upload receipt.",
    receiptPdf: "PDF receipt",
  },
  catalog: {
    title: "Catalog",
    subtitle: "Your products and services portfolio.",
    countItems: "{{count}} items",
    new: "New Item",
    searchPlaceholder: "Search catalog…",
    allItems: "All items",
    activeOnly: "Active only",
    inactiveOnly: "Inactive only",
    allCategories: "All categories",
    noItems: "No catalog items yet. Add your first product or service.",
    deleteTitle: "Delete Item",
    deleteMessage: "Delete \"{{name}}\"? This cannot be undone.",
    name: "Name",
    description: "Description",
    price: "Price",
    unit: "Unit",
    category: "Category",
    status: "Status",
    created: "Item created.",
    deleted: "Item deleted.",
    updated: "Item updated.",
    totalItems: "Total Items",
    activeItems: "Active Items",
    catalogValue: "Active Catalog Value",
    catalogValueSub: "Sum of active item prices",
    newItem: "New Catalog Item",
    editItem: "Edit Item",
    createItem: "Create Item",
    saveChanges: "Save Changes",
    namePlaceholder: "Website Design",
    activeLabel: "Active (visible in sales)",
    perUnit: "per {{unit}}",
    addedBy: "Added by",
    createdAt: "Created",
    back: "Catalog",
    unitUnit: "Unit",
    unitHour: "Hour",
    unitDay: "Day",
    unitMonth: "Month",
    unitLicense: "License",
    unitOther: "Other",
    catProduct: "Product",
    catService: "Service",
    catDigital: "Digital",
    catSubscription: "Subscription",
    catBundle: "Bundle",
    catConsulting: "Consulting",
    catOther: "Other",
    inventoryTitle: "Inventory",
    inventoryStock: "Stock",
    inventoryMinStock: "Min stock",
    inventoryView: "View inventory →",
    lastUpdate: "Last update",
  },
  inventory: {
    title: "Inventory",
    subtitle: "Track stock levels for your products.",
    addItem: "Add Item",
    newItem: "New Inventory Item",
    editItem: "Edit Inventory Item",
    saveChanges: "Save Changes",
    createItem: "Create Item",
    catalogItem: "Product",
    selectProduct: "Select a product…",
    noProducts: "No active products found. Add a product to the catalog first.",
    quantity: "Current Stock",
    minQuantity: "Min Stock",
    minQuantityHint: "Alert when below this threshold (0 = no alert)",
    searchPlaceholder: "Search products…",
    filterLowStock: "Low stock only",
    allStockStatuses: "All stock statuses",
    stockStatus: {
      outOfStock: "Out of stock",
      belowRop: "Below ROP",
      healthy: "Healthy",
    },
    noItems: "No inventory items yet.",
    colProduct: "Product",
    colUnit: "Unit",
    colQuantity: "Stock",
    colMinQty: "Min",
    colUpdated: "Last update",
    colStatus: "Status",
    colActions: "Actions",
    statusOk: "In stock",
    statusLowStock: "Low stock",
    statusOutOfStock: "Out of stock",
    adjust: "Adjust",
    viewMovements: "View movement history",
    movementsTitle: "Movement History",
    noMovements: "No movements recorded yet.",
    fromExpense: "Expense",
    fromSale: "Sale",
    movTypeIn: "Stock In",
    movTypeOut: "Stock Out",
    movTypeAdjustment: "Adjustment",
    adjustTitle: "Adjust Stock",
    currentStock: "Current stock",
    adjustDelta: "Quantity change",
    adjustDeltaHint: "Use a positive number to add stock, negative to remove.",
    adjustNotes: "Notes",
    applyAdjustment: "Apply",
    adjusted: "Stock adjusted successfully.",
    created: "Inventory item created.",
    updated: "Inventory item updated.",
    deleted: "Inventory item removed.",
    deleteConfirmTitle: "Remove inventory item?",
    deleteConfirmMessage: "This will delete the inventory record for \"{{name}}\" and its movement history.",
    kpiTotalItems: "Tracked Items",
    kpiTotalItemsSub: "Products with inventory",
    kpiLowStock: "Low Stock",
    kpiLowStockSub: "Below minimum threshold",
    kpiLowStockAria: "Filter to low-stock items",
    kpiInStock: "In Stock",
    kpiInStockAria: "Filter to in-stock (healthy) items",
    kpiOutOfStock: "Out of Stock",
    kpiOutOfStockAria: "Filter to out-of-stock items",
    kpiStockValue: "Stock Value",
    kpiStockValueSub: "Qty × catalog price",
    inventoryItem: "Inventory Item",
    inventoryQtyReceived: "Qty Received",
    inventoryItemHint: "Link to a product inventory to track stock",
    noInventoryItems: "No inventory items available",
    notFound: "Inventory item not found.",
    backToList: "Back to inventory",
    productInfo: "Product Information",
    category: "Category",
    timestamps: "Timestamps",
    createdAt: "Created",
    updatedAt: "Last updated",
    colType: "Type",
    colChange: "Change",
    colSource: "Source",
    colNotes: "Notes",
    colBy: "By",
    colDate: "Date",
  },
  categories: {
    manage: "Manage Categories",
    catalogTitle: "Catalog Categories",
    expenseTitle: "Expense Categories",
    systemCategory: "System category — cannot be modified",
    newPlaceholder: "New category name…",
    add: "Add",
    confirmDelete: "Delete this category?",
    /** System category labels referenced by `formatCategoryLabel`. */
    expense: {
      office: "Office",
      travel: "Travel",
      meals: "Meals",
      software: "Software",
      equipment: "Equipment",
      marketing: "Marketing",
      other: "Other",
    },
    catalog: {
      product: "Product",
      service: "Service",
      digital: "Digital",
      subscription: "Subscription",
      bundle: "Bundle",
      consulting: "Consulting",
      other: "Other",
    },
  },
  employees: {
    title: "Employees",
    subtitle: "Manage your team members and their roles.",
    totalEmployees: "Total Employees",
    invite: "Invite Employee",
    name: "Name",
    email: "Email",
    role: "Role",
    status: "Status",
    actions: "Actions",
    edit: "Edit",
    deactivate: "Deactivate",
    noEmployees: "No employees yet. Invite someone to get started.",
    deactivateTitle: "Deactivate employee?",
    deactivateConfirm: "Deactivate {{name}}?",
    deactivateMessage: "Deactivate {{name}}? They won't be able to sign in until reactivated. Sales and expenses they recorded will continue to show their name.",
    deactivated: "Employee deactivated.",
    reactivate: "Reactivate",
    reactivateTitle: "Reactivate employee?",
    reactivateMessage: "Reactivate {{name}}? They'll regain sign-in access immediately.",
    reactivated: "Employee reactivated.",
    roleUpdated: "Role updated.",
    admin: "Admin",
    manager: "Manager",
    salesRep: "Sales Rep",
    ofTotal: "of {{total}} total",
    editTitle: "Edit Employee",
    firstName: "First Name",
    lastName: "Last Name",
    adminOnly: "Team management is available to business admins only. Ask an admin if you need a change here.",
    pendingInvites: "Pending invitations",
    resendInvite: "Resend invitation",
    revokeInvite: "Revoke invitation",
    revokeTitle: "Revoke invitation?",
    revokeMessage: "Revoke the invitation for {{email}}? Their invite link stops working immediately.",
    expiresOn: "Expires {{date}}",
    inviteSeatLimit: "Your plan allows up to {{max}} team members. Remove a member or upgrade to invite more.",
    inviteEmailFailed: "The invitation was created but the email couldn't be sent. Use Resend from the pending invitations list.",
    inviteModal: {
      title: "Invite Employee",
      subtitle: "Send an invitation email to add a new team member.",
      email: "Email address",
      role: "Role",
      loadingRoles: "Loading roles…",
      send: "Send Invite",
      sending: "Sending…",
      failed: "Failed to send invite.",
    },
  },
  businessSetup: {
    title: "Set up your business",
    subtitle: "Let's get your workspace ready in a few quick steps.",
    stepBasics: "Basics",
    stepDesign: "Design",
    stepTeam: "Team",
    skipForNow: "Skip for now",
    skipInvites: "Continue without inviting",
    finishCta: "Finish & send invites",
    addInvite: "Add another",
    inviteSubtitle: "Invite teammates by email. They'll receive a link to join your business.",
    inviteEmpty: "No invites yet. Click \"Add another\" to invite someone, or skip this step.",
    inviteEmailPlaceholder: "teammate@example.com",
    inviteSent: "Invitation sent",
    inviteSomeFailed: "Some invitations couldn't be sent. Please review and retry.",
    createError: "Failed to create business.",
    finished: "Business ready!",
    finishedWithInvites: "Business ready! Sent {{count}} invitation(s).",
  },
  account: {
    title: "Account Settings",
    subtitle: "Manage your profile and preferences.",
    profileSection: "Profile",
    preferencesSection: "Preferences",
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email Address",
    emailNote: "Contact support to change your email address.",
    language: "Display Language",
    languageSubtitle: "Choose the language for the entire application.",
    langSaved: "Language updated.",
    appearance: "Appearance",
    appearanceSubtitle: "Switch between light and dark interface theme.",
    themeLight: "Light",
    themeDark: "Dark",
    themeSaved: "Theme updated.",
    switchToDark: "Switch to dark mode",
    switchToLight: "Switch to light mode",
    appDesign: "App design",
    appDesignSubtitle: "These settings only affect how the app looks for you.",
    profilePicture: "Profile Picture",
    profilePictureHint: "Click the camera icon to upload a new photo.",
    avatarSaved: "Profile picture updated.",
    avatarError: "Failed to upload profile picture.",
    security: "Security",
    securitySubtitle: "Update the password you use to sign in.",
    password: "Password",
    changePassword: "Change Password",
    changePasswordSubtitle: "Enter your current password and choose a new one.",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmNewPassword: "Confirm New Password",
    passwordHint: "At least 8 characters.",
    currentPasswordRequired: "Enter your current password.",
    passwordTooShort: "Password must be at least 8 characters.",
    passwordsDoNotMatch: "Passwords do not match.",
    passwordChanged: "Password updated.",
    changePasswordError: "Could not update your password. Please try again.",
    updating: "Updating…",
  },
  features: {
    inventory_enabled: {
      label: "Inventory",
      description: "Track stock levels and low-stock alerts.",
    },
    suppliers_enabled: {
      label: "Suppliers",
      description: "Manage suppliers, payment terms, and AP aging.",
    },
    employees_enabled: {
      label: "Team",
      description: "Track employees and per-employee performance.",
    },
    calendar_enabled: {
      label: "Calendar",
      description: "Schedule appointments and events.",
    },
    catalog_enabled: {
      label: "Catalog",
      description: "Define products and services for your sales.",
    },
    section: {
      title: "Features",
      subtitle: "Hide tabs your business doesn't use. Data is preserved when off.",
      toggleHeader: "Optional features",
      toggleSubtitle: "Owner/admin only — these toggles affect everyone in the business.",
    },
    defaultOffBadge: "Off by default for {{type}}",
    confirmDisable: {
      title: "Hide {{feature}}?",
      message: "Your data is preserved — you can re-enable any time. The tab and nav row will disappear immediately.",
      confirmLabel: "Hide",
    },
    disabledPage: {
      title: "{{feature}} is disabled",
      description: "Your business has this feature turned off. Re-enable it in Settings → Business → Features.",
      cta: "Enable in Settings",
    },
  },
  settings: {
    logoAlt: "Business logo",
    changePreset: "Change preset",
    noVisibleFields: "(No visible fields for this entity)",
    computingChanges: "Computing changes…",
    removeSelection: "Remove {{preset}}",
    dragToReorder: "Drag to reorder",
    presetDiffFieldsShown: "Fields shown",
    presetDiffFieldsHidden: "Fields hidden",
    presetDiffRequiredToggles: "Required toggles",
    presetDiffNewCustom: "New custom fields",
    presetDiffWipedCustom: "Custom fields wiped",
    consultingTitle: "Consulting Services",
    consultingSubtitle: "Expert guidance from our team of business specialists",
    consultingContactCta: "Email us",
    consultingScheduleNote: "Tell us about your business and we'll get back to you.",
    consultingMailSubject: "Consulting inquiry",
    consultingMailBody:
      "Hi Innbuilder team,\n\nI'd like to learn more about your consulting services for my business.\n",
    consultingBizDev: "Business Development",
    consultingStrategy: "Strategy & Planning",
    consultingForecasting: "Revenue Forecasting",
    consultingDigital: "Digital Transformation",
    planCustomPricing: "Custom pricing",
    planPerMonth: "/mo",
    planCurrentPlan: "Current plan",
    planMonthsFree: "{{count}} months FREE",
    planUnlimitedUsers: "Unlimited users",
    planOneUserSeat: "1 user seat",
    planUpToUsers: "Up to {{count}} users (expandable)",
    planContactSales: "Contact Sales",
    planRedirecting: "Redirecting…",
    planSelectPlan: "Select plan",
    packUsersCount: "{{count}} users",
    packPriceBreakdown: "Price breakdown",
    packBaseRow: "Base ({{count}} users): ${{price}}/mo",
    packRow: "+{{users}} users (pack {{pack}}): +${{cost}}/mo",
    packTotalRow: "Total: ${{amount}}/mo",
    title: "Business Settings",
    subtitle: "Manage your business information and branding.",
    adminOnly: "Only business administrators can edit these settings.",
    generalSection: "General Information",
    contactSection: "Contact & Online Presence",
    identitySection: "Business Identity",
    name: "Business Name",
    phone: "Phone",
    address: "Address",
    description: "Description",
    descriptionPlaceholder: "Brief description of your business…",
    website: "Website",
    email: "Business Email",
    industry: "Industry",
    logo: "Business Logo",
    logoHint: "Click the camera icon to upload a logo image.",
    logoSaved: "Logo updated.",
    logoError: "Failed to upload logo.",
    taxId: "Tax ID / RFC",
    saved: "Business settings updated.",
    selectIndustry: "Select industry…",
    industryTechnology: "Technology",
    industryRetail: "Retail",
    industryFoodBeverage: "Food & Beverage",
    industryHealthcare: "Healthcare",
    industryEducation: "Education",
    industryFinance: "Finance",
    industryConstruction: "Construction",
    industryConsulting: "Consulting",
    industryMarketing: "Marketing",
    industryLogistics: "Logistics",
    industryRealEstate: "Real Estate",
    industryOther: "Other",
    generalTab: "General",
    accessTab: "Access Control",
    subscriptionTab: "Subscription",
    formFieldsTab: "Form Fields",
    design: {
      subtitle: "Customize how the app looks for everyone in your business.",
      themeSection: "Theme",
      themeHint: "Light or dark interface mode.",
      colorSection: "Primary Color",
      colorHint: "Used for buttons, links, and accents across the app.",
      colorViolet: "Violet",
      colorBlue: "Metallic Blue",
      colorGold: "Golden",
      colorSilver: "Silver",
      fontSection: "Font",
      fontHint: "The typeface used throughout the app.",
      fontSansation: "Sansation",
      fontInter: "Inter",
      fontRoboto: "Roboto",
      fontPoppins: "Poppins",
      fontSystem: "System",
      reset: "Reset to defaults",
    },
    formFields: {
      entityLabel: "Entity:",
      searchPlaceholder: "Search fields…",
      noMatches: "No matching fields.",
      entity: {
        customer: "Customers",
        supplier: "Suppliers",
        expense: "Expenses",
        catalog: "Catalog",
        inventory: "Inventory",
        employee: "Employees",
        sale: "Sales",
      },
      builtinTitle: "Built-in Fields",
      builtinSubtitle: "Control which standard fields appear on the form.",
      customTitle: "Custom Fields",
      customSubtitle: "Add your own fields to collect extra information.",
      visible: "Visible",
      required: "Required",
      alwaysRequired: "(always required)",
      hidden: "hidden",
      addField: "Add Field",
      newField: "New Custom Field",
      fieldLabel: "Label",
      fieldLabelPlaceholder: "e.g. Membership Number",
      fieldType: "Type",
      fieldOptions: "Options (comma-separated)",
      fieldOptionsPlaceholder: "e.g. Bronze, Silver, Gold",
      saveField: "Create Field",
      saved: "Settings saved.",
      saveError: "Failed to save. Please try again.",
      fieldCreated: "Custom field created.",
      fieldDeleted: "Custom field deleted.",
      deleteFieldConfirm: "Delete \"{{label}}\"? This will remove its values from all records.",
      noCustomFields: "No custom fields yet. Add one above.",
      fieldTypes: {
        text: "Text",
        textarea: "Multiline Text",
        number: "Number",
        date: "Date",
        select: "Dropdown (Select)",
        email: "Email",
        phone: "Phone",
        checkbox: "Checkbox",
        file: "File",
      },
      field: {
        customer: {
          name: "Name",
          email: "Email",
          phone: "Phone",
          address: "Address",
          notes: "Notes",
          date_of_birth: "Date of Birth",
          city: "City",
          state: "State / Province",
          country: "Country",
          customer_type: "Customer Type",
          company: "Company",
          tax_id: "Tax ID",
          website: "Website",
          language_preference: "Language",
          referral_source: "Referral Source",
          marketing_opt_in: "Marketing Opt-in",
          preferred_contact_method: "Preferred Contact Method",
          loyalty_id: "Loyalty ID",
          vip_tier: "VIP Tier",
          allergies: "Allergies",
          insurance_id: "Insurance ID",
          license_plate: "License Plate",
          project_ref: "Project Reference",
        },
        supplier: {
          name: "Name",
          email: "Email",
          phone: "Phone",
          address: "Address",
          contact_person: "Contact Person",
          notes: "Notes",
          website: "Website",
          tax_id: "Tax ID",
          payment_terms: "Payment Terms",
          lead_time_days: "Lead Time (days)",
          preferred_currency: "Preferred Currency",
          bank_account: "Bank Account",
          iban: "IBAN",
          swift: "SWIFT",
          minimum_order: "Minimum Order",
          discount_tier: "Discount Tier",
          category: "Category",
          rating: "Rating",
          certifications: "Certifications",
          on_hold: "On Hold",
        },
        expense: {
          title: "Title",
          amount: "Amount",
          category: "Category",
          expense_date: "Date",
          supplier: "Supplier",
          notes: "Notes",
          payment_method: "Payment Method",
          reference_number: "Reference #",
          tax_amount: "Tax Amount",
          tax_rate: "Tax Rate",
          billable: "Billable",
          project_ref: "Project Reference",
          approved_by: "Approved By",
          reimbursable: "Reimbursable",
          receipt: "Receipt",
          currency: "Currency",
          recurring: "Recurring",
          department: "Department",
        },
        catalog: {
          name: "Name",
          description: "Description",
          category: "Category",
          price: "Price",
          unit: "Unit",
          is_active: "Active",
          sku: "SKU",
          barcode: "Barcode",
          brand: "Brand",
          supplier_default: "Default Supplier",
          cost_price: "Cost Price",
          margin: "Margin",
          weight: "Weight",
          dimensions: "Dimensions",
          tax_class: "Tax Class",
          warranty_months: "Warranty (months)",
          serial_tracked: "Serial Tracked",
          hazard_class: "Hazard Class",
          shelf_life_days: "Shelf Life (days)",
          allergens: "Allergens",
          min_order_qty: "Min Order Qty",
        },
        inventory: {
          quantity: "Quantity",
          min_quantity: "Min Quantity",
          location: "Location",
          lot_number: "Lot #",
          serial_number: "Serial #",
          expiry_date: "Expiry Date",
          last_restocked_at: "Last Restocked",
          reorder_qty: "Reorder Qty",
          reserved_qty: "Reserved Qty",
          max_quantity: "Max Quantity",
          cost_layer: "Cost Layer",
          supplier_ref: "Supplier Ref",
        },
        employee: {
          role: "Role",
          is_active: "Active",
          employee_code: "Employee Code",
          phone: "Phone",
          address: "Address",
          hire_date: "Hire Date",
          date_of_birth: "Date of Birth",
          department: "Department",
          emergency_contact_name: "Emergency Contact",
          emergency_contact_phone: "Emergency Phone",
          id_number: "ID Number",
          tax_id: "Tax ID",
          bank_account: "Bank Account",
          salary: "Salary",
          pay_frequency: "Pay Frequency",
          employment_type: "Employment Type",
          status: "Status",
          termination_date: "Termination Date",
          certifications: "Certifications",
          shirt_size: "Shirt Size",
        },
        sale: {
          status: "Status",
          pay_status: "Payment Status",
          sale_date: "Sale Date",
          notes: "Notes",
          payment_method: "Payment Method",
          reference_number: "Reference #",
          channel: "Channel",
          sales_rep: "Sales Rep",
          project_ref: "Project Reference",
          po_number: "PO Number",
          shipping_method: "Shipping Method",
          delivery_date: "Delivery Date",
          invoice_number: "Invoice Number",
          currency: "Currency",
          notes_internal: "Internal Notes",
        },
      },
      option: {
        customer: {
          customer_type: { individual: "Individual", company: "Company" },
          preferred_contact_method: { email: "Email", phone: "Phone", sms: "SMS" },
          vip_tier: { bronze: "Bronze", silver: "Silver", gold: "Gold", platinum: "Platinum" },
        },
        supplier: {
          payment_terms: { net_15: "Net 15", net_30: "Net 30", net_60: "Net 60", net_90: "Net 90", cod: "Cash on Delivery", prepaid: "Prepaid" },
          discount_tier: { a: "Tier A", b: "Tier B", c: "Tier C", none: "None" },
        },
        expense: {
          payment_method: { cash: "Cash", card: "Card", transfer: "Transfer", check: "Check", other: "Other" },
        },
        catalog: {
          unit: { unit: "Unit", hour: "Hour", day: "Day", month: "Month", license: "License", other: "Other" },
        },
        inventory: {
          cost_layer: { fifo: "FIFO", lifo: "LIFO", average: "Average" },
        },
        employee: {
          pay_frequency: { hourly: "Hourly", weekly: "Weekly", biweekly: "Biweekly", monthly: "Monthly" },
          employment_type: { full_time: "Full-time", part_time: "Part-time", contract: "Contract", intern: "Intern" },
          status: { active: "Active", on_leave: "On Leave", terminated: "Terminated" },
          shirt_size: { xs: "XS", s: "S", m: "M", l: "L", xl: "XL", xxl: "XXL" },
        },
        sale: {
          status: { draft: "Draft", confirmed: "Confirmed", cancelled: "Cancelled" },
          pay_status: { unpaid: "Unpaid", partial: "Partial", paid: "Paid" },
          payment_method: { cash: "Cash", card: "Card", transfer: "Transfer", check: "Check", other: "Other" },
          channel: { in_store: "In-store", online: "Online", phone: "Phone" },
        },
      },
    },
    accessControlSubtitle: "Manage what each role can see and do across sections.",
    rolesTitle: "Roles",
    addRole: "Add role",
    roleNamePlaceholder: "Role name…",
    deleteRole: "Delete role",
    deleteRoleConfirm: "Delete role \"{{name}}\"? Employees with this role will need to be reassigned.",
    systemRole: "System",
    customRole: "Custom",
    permView: "View",
    permEdit: "Edit",
    permNone: "None",
    saveRolePermissions: "Save",
    permissionsSaved: "Permissions saved.",
    roleCreated: "Role created.",
    roleDeleted: "Role deleted.",
    cannotDeleteSystemRole: "System roles cannot be deleted.",
    roleHasEmployees: "Cannot delete: {{count}} active employee(s) have this role.",
    lockTooltip: "Admin access to Settings cannot be removed.",
    sectionColumn: "Section",
    unsavedChanges: "Unsaved changes",
    selectRolePrompt: "Select a role to view and edit its permissions.",
    rolesListHeading: "Roles",
    permissionsHeading: "Permissions",

    // Form Fields preset system
    formPresets: {
      sectionHeading: "Pick the business type that matches yours",
      sectionSubtitle: "Pick your business type, then choose how much detail you want. You can fine-tune individual fields afterward.",
      choose: "Choose",
      changeLevel: "Change level",
      backToTypes: "Back to business types",
      levelHeading: "{{type}} — pick level of detail",
      levelSubtitle: "Pick the level that best matches what your business actually needs to track.",
      pickLevel: "Pick {{level}}",
      reapplyLevel: "Re-apply {{level}}",
      currentLevel: "Current",
      currentlyApplied: "In use",
      fieldCount: "{{count}} field",
      fieldCount_other: "{{count}} fields",
      upToFields: "Up to {{count}} fields",
      customTag: "custom",
      seeFields: "See fields",
      hideFields: "Hide fields",
      tabsYoullSee: "Tabs you'll see",
      tabPreview: {
        overview: "Overview",
        sales: "Sales",
        expenses: "Expenses",
        customers: "Customers",
        inventory: "Inventory",
        suppliers: "Suppliers",
        catalog: "Catalog",
        calendar: "Calendar",
        team: "Team",
      },
      levelDescription: {
        essential: "The fields most businesses in this category actually use.",
        full: "Adds the deeper fields for power users — comprehensive without going exhaustive.",
      },
      searchPlaceholder: "Search business types…",
      comparePresets: "Compare presets",
      customizeFields: "Customize fields",
      backToPreset: "Back to {{type}} — {{level}}",
      resetToPreset: "Reset to preset",
      resetBannerBody: "You've customized this from the {{type}} — {{level}} preset.",
      applyTitle: "Apply {{type}} — {{level}}",
      applyBody: "This will configure all modules to match the preset.",
      applyConfirm: "Apply preset",
      switchTitle: "Switch to {{type}} — {{level}}",
      switchWarning: "Switching presets will wipe your customizations across all modules and re-apply the new preset.",
      switchConfirm: "Switch preset",
      previewLabel: "Live preview",
      previewSubtitle: "How this form will look to your team.",
      currentPresetLabel: "Current preset",
      noPresetAppliedTitle: "No preset applied yet",
      noPresetAppliedBody: "Pick a business type below to configure your forms in one click.",
      modifiedBadge: "Customized",
      levels: {
        essential: "Essential",
        full: "Full",
      },
      industry: {
        spa:        { name: "Spa",                            tagline: "Treatments + appointments" },
        salon:      { name: "Salon / Barbershop",             tagline: "Walk-ins + booked appointments" },
        gym:        { name: "Gym / Fitness studio",           tagline: "Memberships + classes" },
        ice_cream:  { name: "Ice cream shop",                 tagline: "Walk-in counter + flavor inventory" },
        cafe:       { name: "Cafe",                           tagline: "Quick service + daily prep" },
        bakery:     { name: "Bakery",                         tagline: "Daily baked goods + custom orders" },
        restaurant: { name: "Restaurant",                     tagline: "Sit-down service + ingredients" },
        retail:     { name: "Retail store",                   tagline: "Product catalog + SKU stock" },
        consulting: { name: "Consulting / Agency",            tagline: "Project clients + invoicing" },
        tutoring:   { name: "Tutoring / Coaching",            tagline: "Recurring sessions + students" },
        repair:     { name: "Repair / Field service",         tagline: "Field jobs + parts" },
        clinic:     { name: "Clinic / Doctor's consultorio",  tagline: "Patient appointments + records" },
        general:    { name: "General",                        tagline: "Catch-all default" },
      },
      compare: {
        title: "Compare presets",
        addPreset: "Add preset to compare",
        clearAll: "Clear all",
        empty: "Pick at least one preset to compare.",
        statusHidden: "Hidden",
        statusVisible: "Visible",
        statusRequired: "Required",
      },
      forcePicker: {
        title: "Pick your business type",
        body: "Your current fields are preserved as customizations on top of the preset you pick. You can change presets later.",
        browse: "Browse presets",
        keep: "Keep what I have",
      },
      onboarding: {
        skip: "Skip for now — use the General default",
      },
    },
    sectionDashboard: "Dashboard",
    sectionCustomers: "Customers",
    sectionSuppliers: "Suppliers",
    sectionSales: "Sales",
    sectionExpenses: "Expenses",
    sectionCatalog: "Catalog",
    sectionInventory: "Inventory",
    sectionEmployees: "Employees",
    sectionSettings: "Settings",
    sectionCalendar: "Calendar",
    sectionNotifications: "Notifications",
    templateBasedOn: "Based on {{name}}",
    templateModified: "modified",
    resetToTemplate: "Reset to template",
    templateNone: "Blank",
    templateAdmin: "Admin",
    templateManager: "Manager",
    templateSalesRep: "Sales Rep",
    templatePickerLabel: "Start from",
    localizationSection: "Regional",
    currency: "Currency",
    timezone: "Timezone",
    language: "Language",
    languageEnglish: "English",
    languageSpanish: "Español",
    localizationHint: "Currency, timezone, and default UI language for everyone working in this business.",
  },
  export: {
    title: "Export Data",
    subtitle: "Choose a date range and file format.",
    startDate: "Start Date",
    endDate: "End Date",
    format: "Format",
    download: "Download",
    downloading: "Downloading…",
    success: "File downloaded.",
    failed: "Export failed. Please try again.",
  },
  platform: {
    title: "Platform Analytics",
    subtitle: "Creator-only view of platform-wide usage.",
  },
  errors: {
    loadFailed: "Failed to load: {{detail}}",
    notFound: "Not found.",
  },
  landing: {
    meta: {
      title: "Adam — Run your business from a single platform",
      description:
        "Run your business from a single platform. Sales, customers, inventory, expenses and goals — all your information, ready to grow. Free during early access.",
    },
    consent: {
      aria: "Analytics notice",
      message:
        "We use first-party analytics to understand how our landing page is used and improve the product — no third-party or advertising trackers. You can opt out anytime.",
      gotIt: "Got it",
      manage: "Manage",
      learnMore: "Cookie Policy",
    },
    cookiePrefs: {
      title: "Cookie preferences",
      intro:
        "Manage how Adam uses cookies and similar browser storage. Essential items keep the site working; analytics are first-party only and help us improve the product.",
      essentialLabel: "Essential",
      essentialDesc:
        "Required for sign-in, security, and remembering your preferences. Always on.",
      analyticsLabel: "Analytics",
      analyticsDesc:
        "First-party audience measurement of our marketing pages. No third parties, no cross-site tracking.",
      save: "Save preferences",
      acceptAll: "Accept all",
      essentialOnly: "Essential only",
      close: "Close",
    },
    nav: {
      features: "Features",
      tour: "Product tour",
      verticals: "Industries",
      earlyAccess: "Early access",
      faq: "FAQ",
      login: "Log in",
      getStarted: "Get started free",
    },
    hero: {
      badge: "Early access — free for every business",
      headlineLine1: "Run your business",
      headlineAccent: "from a single platform.",
      subheadline: "All your information — sales, customers, inventory, expenses and goals — instantly available and ready to fuel your growth.",
      ctaPrimary: "Start free today",
      ctaSecondary: "Explore the product",
      earlyAccessNote: "Free during early access · every module included · no credit card",
      proof: ["8 connected modules", "Your whole team included", "Export your data anytime"],
    },
    demo: {
      labels: {
        revenueTrend: "Revenue trend",
        paid: "Paid",
        partial: "Partial",
        unpaid: "Pending",
      },
    },
    bento: {
      rent: "Rent",
      supplies: "Supplies",
      goal: "Monthly goal",
      admin: "Admin",
      sales: "Sales rep",
    },
    verticals: {
      tag: "Built for your industry",
      title: "What does your business do?",
      subtitle: "Pick your line of business and see how Adam fits it — at signup we pre-load matching sample products and a starter goal.",
      tiles: {
        retail: {
          title: "Retail & stores",
          headline: "Stop reconciling inventory in WhatsApp screenshots.",
          body: "Sell from the register, track stock in real time, and know which product pays the rent — without juggling spreadsheets.",
          bullets: [
            "Live inventory on every sale",
            "Supplier orders + low-stock alerts",
            "Customer history with one tap",
          ],
        },
        food_beverage: {
          title: "Food & beverage",
          headline: "Know your food cost before the month closes.",
          body: "Suppliers, expenses and daily sales in one place. Spot the ingredient that's quietly eating your margin.",
          bullets: [
            "Suppliers & expenses connected",
            "A daily close you can trust",
            "Ingredient stock under control",
          ],
        },
        healthcare: {
          title: "Health & clinics",
          headline: "Your practice organized — no chasing payments.",
          body: "Patients, appointments and charges in one place, with every patient's history one tap away.",
          bullets: [
            "Every patient's history and details",
            "Appointments and reminders on the calendar",
            "Clear payments and pending balances",
          ],
        },
        consulting: {
          title: "Consulting & services",
          headline: "Bill every project on time — no folder maze.",
          body: "Clients, engagements and collections organized, with each project's real numbers always visible.",
          bullets: [
            "Clients and projects in order",
            "Partial payments and clear balances",
            "Billing goals that track themselves",
          ],
        },
        marketing: {
          title: "Marketing & agencies",
          headline: "Every account's real margin — without opening five files.",
          body: "Bill per client, track spending per campaign, and know which account actually makes money.",
          bullets: [
            "Income and expenses per client",
            "Recurring services under control",
            "Goals and reports for your team",
          ],
        },
        technology: {
          title: "Technology",
          headline: "Licenses, services and clients in one clean system.",
          body: "Recurring charges, open balances and monthly growth — visible without building a dashboard by hand.",
          bullets: [
            "Recurring services and licenses",
            "Balances without chasing emails",
            "Monthly growth on your dashboard",
          ],
        },
      },
      footnote: "A different business? Adam works for any small business — the samples are just a head start.",
    },
    capabilities: {
      tag: "Capabilities",
      title: "Everything your business touches, in one place.",
      subtitle: "Start with sales and customers — switch on the rest whenever you're ready.",
      items: {
        dashboard: { title: "Live dashboard", body: "Revenue, expenses and trends, updated with every sale you log." },
        sales: { title: "Sales & payments", body: "Line items, partial payments, and every peso owed — tracked." },
        customers: { title: "Customers", body: "Profiles, purchase history and lifetime value in one tap." },
        inventory: { title: "Inventory & catalog", body: "Products with prices, and stock that updates itself on every sale." },
        expenses: { title: "Expenses", body: "One-time and recurring spending, with receipts attached." },
        team: { title: "Team, roles & goals", body: "Invite your team with the right permissions, and set goals that track themselves." },
      },
    },
    tour: {
      tag: "Product tour",
      title: "This is your business, at a glance.",
      subtitle: "Real screens from real demo businesses — the same dashboard you get on day one.",
      expand: "View full size",
      close: "Close",
      shots: {
        overview: {
          label: "Dashboard",
          title: "Every number that matters, on one screen.",
          body: "Revenue, expenses, profit and active goals — updated with every sale your team logs, filtered to any date range.",
          alt: "Adam dashboard overview showing revenue and expense KPIs, a trend chart, and active goals",
        },
        team: {
          label: "Team",
          title: "Collaborate with your whole team.",
          body: "Invite employees with the right role — admin, manager, sales rep or your own — so everyone sees exactly what they need and every sale they log feeds the same picture.",
          alt: "Employees page listing team members with their roles and permissions",
        },
        sales: {
          label: "Sales",
          title: "Every sale, payment and balance — tracked.",
          body: "Log sales with line items and partial payments, see what's paid and what's owed, and never lose track of a single receivable.",
          alt: "Sales section listing sales with amounts, payment status, and balances",
        },
        calendar: {
          label: "Calendar",
          title: "Your business schedule, in the same place.",
          body: "Deliveries, appointments and team events on one calendar — connected to the sales and customers they belong to.",
          alt: "Calendar month view with business events and appointments",
        },
        goals: {
          label: "Goals",
          title: "Set goals that track themselves.",
          body: "Monthly revenue, ticket count, new customers — pick a target and watch progress update with every sale, no spreadsheets.",
          alt: "Goals page with active goals and self-updating progress bars",
        },
      },
    },
    how: {
      tag: "How it works",
      title: "From signup to first sale in 3 minutes.",
      steps: [
        { title: "Sign up", body: "Email + password. One screen." },
        { title: "Tell us your industry", body: "We pre-configure categories, products, and a starter goal." },
        { title: "60-second tour", body: "A quick walkthrough of what's where." },
        { title: "Log your first sale", body: "Your dashboard is alive. Everything else is optional." },
      ],
    },
    earlyAccess: {
      tag: "Early access",
      title: "Free while we build it with you.",
      subtitle: "Adam is in early access: every business gets the full product, free.",
      badge: "Early access",
      price: "Free",
      priceNote: "during early access — every module included",
      features: [
        "All 8 modules, nothing locked",
        "Unlimited sales, customers and products",
        "Your whole team, roles included",
        "Live dashboards & goals",
        "Ready in minutes",
        "Export your data anytime",
      ],
      cta: "Create your free account",
      finePrint: "No credit card. No surprises — you'll always be told before anything changes.",
      afterNote: "Paid plans will be announced well in advance. Early-access users hear first.",
    },
    faq: {
      tag: "FAQ",
      title: "Questions, answered.",
      items: [
        {
          q: "Is Adam really free right now?",
          a: "Yes. Adam is in early access and every feature is free while we polish the product with our first businesses. No credit card, no fine print.",
        },
        {
          q: "What happens when early access ends?",
          a: "We'll announce paid plans well in advance, and early-access users will hear first. You'll never be charged silently, and you can export your data at any time.",
        },
        {
          q: "Can I leave whenever I want?",
          a: "Yes. You can stop using Adam at any time and export your information (CSV/Excel) before you go. Your data is yours.",
        },
        {
          q: "Is my data safe?",
          a: "We take care of it: connections are encrypted, access is protected with roles and permissions, and only your team can see your business. The details are in our Privacy Policy and Terms of Service.",
        },
        {
          q: "How long does setup take?",
          a: "Minutes. Pick your industry and Adam pre-loads sample products and categories so you can log your first sale right away — then clear the samples when you're done exploring.",
        },
        {
          q: "Can my team use it too?",
          a: "Yes — invite employees by email and give each one a role (admin, manager, sales rep, or your own custom roles) so they see exactly what they should.",
        },
        {
          q: "Does it work on my phone, in Spanish?",
          a: "Both. Adam runs in the browser on your phone, tablet or computer, in English and Spanish — switch languages anytime.",
        },
      ],
    },
    finalCta: {
      eyebrow: "Early access · free · no card",
      title: "Stop juggling tools.",
      titleLine2: "Run your business on one screen.",
      subtitle: "Join the small businesses replacing spreadsheets, chats and notebooks with Adam — free during early access.",
      button: "Get started free",
      secondary: "Talk to us",
    },
    footer: {
      tagline: "One place for your sales, customers, inventory and goals — built for small businesses.",
      columns: {
        product: "Product",
        legal: "Legal",
        contact: "Contact",
      },
      links: {
        features: "Features",
        verticals: "Solutions by industry",
        earlyAccess: "Early access",
        faq: "FAQ",
        consulting: "Consulting",
        contact: "Contact us",
        terms: "Terms of Service",
        privacy: "Privacy Policy",
        aup: "Acceptable Use",
        dpa: "Data Processing",
        subprocessors: "Subprocessors",
        cookies: "Cookies",
        cookiePreferences: "Cookie preferences",
        login: "Log in",
      },
      copyright: "© {{year}} Adam · Innbuilder. All rights reserved.",
    },
  },
  onboarding: {
    badge: "New business setup",
    title: "Let's set up your Adam workspace",
    subtitle: "Three quick answers and you're in. You can polish everything else later from the dashboard.",
    fields: {
      name: "Business name",
      industry: "Pick an industry",
      country: "Country",
      currency: "Currency",
      timezone: "Timezone",
      language: "Language",
    },
    placeholders: {
      name: "e.g. Café Luna",
    },
    countryOther: "Other",
    timezoneOther: "Other…",
    timezoneSearch: "Search timezones…",
    setupError: {
      title: "We couldn't load your workspace",
      body: "Something went wrong reaching the server. Your data is safe — try again in a moment.",
      retry: "Try again",
      retrying: "Retrying…",
    },
    presetSkipApplied:
      "Applied the General / Essential form defaults. Change anytime in Settings → Forms.",
    timezoneHelp: "We use this for everything that displays a date or time.",
    currencyHelp: "Used for every amount you record. You can change it later in settings.",
    languageHelp: "The default UI language for everyone in this business. Each user can still override their own preference.",
    validation: {
      nameRequired: "Give your business a name to continue.",
      industryRequired: "Pick the closest industry so we can set you up.",
    },
    created: "Welcome in!",
    creating: "Setting things up…",
    continue: "Continue",
    privacyNote: "By continuing you agree your data will be processed per the Privacy Policy. You're the owner of everything you enter — export or delete any time.",
    nextStepsHint: "Included automatically",
    deferred: {
      starterData: "A starter setup if you pick the samples option",
      tour: "A 60-second product tour if you pick the guided option",
      customize: "Logo, theme, team invites — one-click cards on your dashboard",
    },
    modeModal: {
      title: "How do you want to start?",
      subtitle: "Pick one — you can switch any time from your dashboard.",
      blank: {
        title: "Start blank",
        body: "A clean workspace. Add your own customers, products, and sales.",
      },
      samples: {
        title: "Explore with samples",
        body: "We'll preload sample customers, products, and sales so you can click around. You can clear them with one click later.",
      },
      guided: {
        title: "Guided tour",
        body: "We'll walk you through the key sections in 60 seconds.",
      },
      saving: "Setting up…",
    },
    industries: {
      retail: {
        label: "Retail & stores",
        examples: "Boutique · Ferretería · Tienda de abarrotes",
      },
      food_beverage: {
        label: "Food & beverage",
        examples: "Café · Restaurante · Panadería",
      },
      services: {
        label: "Services",
        examples: "Salón · Barbería · Estudio",
      },
      healthcare: {
        label: "Health & wellness",
        examples: "Clínica · Dentista · Spa",
      },
      education: {
        label: "Education",
        examples: "Academia · Tutor · Escuela",
      },
      consulting: {
        label: "Consulting",
        examples: "Agencia · Freelance · Asesoría",
      },
      construction: {
        label: "Construction & trades",
        examples: "Contratista · Taller · Mantenimiento",
      },
      marketing: {
        label: "Marketing & creative",
        examples: "Estudio · Foto · Video",
      },
      other: {
        label: "Something else",
        examples: "Tell us in the dashboard",
      },
    },
    checklist: {
      title: "Getting started",
      subtitle: "A quick punch list to make Adam yours",
      progress: "{{done}} of {{total}} complete",
      collapsed: "Getting started · {{percent}}%",
      dismissHint: "Hide until I finish",
      items: {
        has_customer: { title: "Add your first real customer", cta: "Add customer" },
        has_product: { title: "Add a product or service", cta: "Open catalog" },
        has_sale: { title: "Log your first sale", cta: "Log sale" },
        has_teammate: { title: "Invite a teammate", cta: "Invite" },
        has_goal: { title: "Set a goal for this month", cta: "Add goal" },
        has_logo: { title: "Upload your logo", cta: "Upload" },
      },
    },
    starterBanner: {
      title: "Starter sample data",
      body: "We pre-filled a few sample items so your dashboard looks alive. Clear them whenever you're ready — one click.",
      cta: "Clear samples",
      clearing: "Clearing…",
      cleared: "Starter samples cleared.",
    },
    tour: {
      skip: "Skip tour",
      next: "Next",
      back: "Back",
      finish: "Got it",
      step: "Step {{n}} of {{total}}",
      steps: {
        welcome: {
          title: "Welcome to Adam",
          body: "A 60-second tour of your workspace. You can re-open it any time from the dashboard.",
        },
        sidebar: {
          title: "Your modules",
          body: "Everything your business needs — customers, sales, inventory, goals — one sidebar, one login.",
        },
        dashboard: {
          title: "Live dashboard",
          body: "Every metric updates as you log activity. No waiting, no exports.",
        },
        add: {
          title: "Fast add",
          body: "Create a customer, log a sale, or post an expense in seconds from anywhere.",
        },
        checklist: {
          title: "Getting-started checklist",
          body: "Tick off the first five actions and your dashboard is ready for real work.",
        },
      },
    },
    deferredCards: {
      title: "Finish customizing later",
      subtitle: "A few optional steps — or skip them forever.",
      dismissCard: "Dismiss this card",
      logo: { title: "Upload your logo", body: "Badge receipts, invoices, and the sidebar.", cta: "Upload" },
      theme: { title: "Pick your colors", body: "Match your brand across the product.", cta: "Customize" },
      address: { title: "Add your address", body: "Needed for invoices and receipts.", cta: "Add" },
      invites: { title: "Invite your team", body: "Roles and permissions ready out of the box.", cta: "Invite" },
      dismiss: "Not now",
      dismissed: "Hidden. Re-open from Settings → Workspace.",
    },
  },
  legal: {
    common: {
      legalCenter: "Legal Center",
      contents: "Contents",
      backToHome: "Back to home",
      lastUpdatedOn: "Last updated {{date}}",
    },
    nav: {
      terms: "Terms of Service",
      privacy: "Privacy Policy",
      aup: "Acceptable Use",
      dpa: "Data Processing",
      subprocessors: "Subprocessors",
      cookies: "Cookie Policy",
    },
    subprocessors: {
      col: {
        provider: "Provider",
        purpose: "Purpose",
        data: "Data categories",
        region: "Region",
      },
    },
    cookies: {
      col: {
        name: "Cookie",
        provider: "Provider",
        purpose: "Purpose",
        category: "Category",
        duration: "Duration",
      },
      cat: {
        essential: "Essential",
        functional: "Functional",
        analytics: "Analytics",
      },
    },
    acceptance: {
      checkboxLabel: "I agree to the <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy>.",
      checkboxRequired: "You must accept the Terms and Privacy Policy to continue.",
      updatedTitle: "We've updated our legal documents",
      updatedBody: "Our Terms of Service and Privacy Policy have been updated. Please review and accept to continue using Adam.",
      reviewTerms: "Review Terms",
      reviewPrivacy: "Review Privacy",
      acceptAndContinue: "Accept and continue",
      logout: "Log out",
    },
  },
  terms: {
    nav: {
      backToHome: "Back to home",
    },
    title: "Terms and Conditions",
    lastUpdated: "Last updated: April 15, 2026",
    tableOfContents: "Table of Contents",
    sections: {
      s1Title: "1. Acceptance of Terms",
      s1Body: "By accessing or using the Adam platform (\"Service\"), operated by [FULL_LEGAL_NAME], a persona física registered with Mexico's SAT under the Régimen Simplificado de Confianza (RFC: [RFC]; domicilio fiscal: [FISCAL_ADDRESS], [CITY], [STATE], México), doing business under the trade name \"Innbuilder\" (\"Innbuilder\", \"we\", \"us\", or \"our\"), you agree to be bound by these Terms and Conditions (\"Terms\"). The Service is offered exclusively to businesses for use in their commercial activity (B2B). If you do not agree to all of these Terms, do not access or use the Service.\n\nThese Terms apply to all users of the Service, including without limitation users who are browsers, customers, merchants, and contributors of content. By using the Service, you represent that you are at least 18 years of age and have the legal authority to enter into a binding agreement.",
      s2Title: "2. Description of Services",
      s2Body: "Adam is a cloud-based business management platform that provides tools for managing customers, sales, expenses, inventory, catalogs, employees, and business analytics (\"Service\"). The Service is provided on a subscription basis through our website and associated web applications.\n\nWe reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time with or without notice. We shall not be liable to you or to any third party for any modification, suspension, or discontinuation of the Service.\n\nCertain features of the Service may be subject to additional terms or guidelines, which are incorporated herein by reference.",
      s3Title: "3. User Accounts & Registration",
      s3Body: "To access the Service, you must register for an account by providing accurate, current, and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.\n\nYou agree to notify us immediately at support@adambusinesshub.com of any unauthorized use of your account or any other breach of security. We cannot and will not be liable for any loss or damage arising from your failure to comply with this security obligation.\n\nYou may not use another person's account without permission. Account registration is limited to one account per person; you may not create multiple accounts.\n\nWe reserve the right to refuse service, terminate accounts, or remove or edit content at our sole discretion.",
      s4Title: "4. Subscription & Payment Terms",
      s4Body: "Access to certain features of the Service requires a paid subscription. Subscription fees are billed in advance on a monthly or annual basis, depending on the plan you select. All fees are non-refundable except as expressly stated in these Terms or as required by applicable law.\n\nWe use third-party payment processors (including Stripe) to handle billing. By providing payment information, you authorize us to charge the applicable fees to your payment method. You represent that you have the legal authority to use the payment method provided.\n\nSubscription fees are subject to change. We will notify you of any fee changes at least 30 days in advance. Continued use of the Service after the fee change takes effect constitutes your agreement to pay the modified fee.\n\nIf your payment fails, we may suspend or terminate your access to the Service until payment is received. Past-due amounts may be subject to collection proceedings.",
      s5Title: "5. Free Trial Terms",
      s5Body: "We offer a free trial period of three (3) months for new subscribers. During the free trial, you will have access to the features included in your selected plan at no charge.\n\nAt the end of the free trial period, your subscription will automatically convert to a paid subscription unless you cancel before the trial expires. You will be charged the applicable subscription fee at the beginning of the first billing cycle following the free trial.\n\nWe reserve the right to modify or terminate the free trial offer at any time without notice. Only one free trial is permitted per business entity or individual.",
      s6Title: "6. Acceptable Use Policy",
      s6Body: "You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to use the Service: (a) in any way that violates any applicable local, national, or international law or regulation; (b) to transmit any unsolicited or unauthorized advertising or promotional material; (c) to impersonate or attempt to impersonate the Company, a Company employee, another user, or any other person; (d) to engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Service.\n\nYou agree not to: upload or transmit viruses or any other type of malicious code; attempt to gain unauthorized access to any portion of the Service; use any robot, spider, or other automatic device to access the Service for any purpose; interfere with or disrupt the integrity or performance of the Service.\n\nWe reserve the right to terminate your access to the Service for violations of this Acceptable Use Policy.",
      s7Title: "7. Intellectual Property",
      s7Body: "The Service and its original content, features, and functionality are and will remain the exclusive property of [FULL_LEGAL_NAME] (operating as \"Innbuilder\") and our licensors. The Service is protected by copyright, trademark, and other laws of Mexico and foreign countries.\n\nThe trademarks, trade dress, and trade names of \"Innbuilder\" and \"Adam\" may not be used in connection with any product or service without our prior written consent.\n\nYou retain all rights to the data and content you upload to the Service (\"User Content\"). By uploading User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, copy, process, and display your User Content solely for the purpose of providing the Service to you.\n\nYou represent and warrant that you own or have the necessary rights to your User Content and that it does not infringe any third-party intellectual property rights.",
      s8Title: "8. Privacy & Data",
      s8Body: "Your privacy is important to us. Our Privacy Policy, which is incorporated into these Terms by reference, explains how we collect, use, and share information about you when you use the Service. By using the Service, you consent to our collection and use of personal data as outlined in our Privacy Policy.\n\nWe implement industry-standard security measures to protect your data, including encryption in transit (TLS) and at rest. However, no method of transmission over the Internet or method of electronic storage is 100% secure.\n\nYou may request deletion of your account and associated data at any time by contacting us at support@adambusinesshub.com. We will process deletion requests within 30 days, subject to any legal obligations to retain certain data.",
      s9Title: "9. Disclaimers & Warranties",
      s9Body: "THE SERVICE IS PROVIDED ON AN \"AS IS\" AND \"AS AVAILABLE\" BASIS WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.\n\nWe do not warrant that: (a) the Service will function uninterrupted, secure, or available at any particular time or location; (b) any errors or defects will be corrected; (c) the Service is free of viruses or other harmful components; (d) the results of using the Service will meet your requirements.\n\nSome jurisdictions do not allow the exclusion of implied warranties, so the above exclusions may not apply to you.",
      s10Title: "10. Limitation of Liability",
      s10Body: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL INNBUILDER, ITS AFFILIATES, DIRECTORS, EMPLOYEES, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM: (i) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE; (ii) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE; (iii) ANY CONTENT OBTAINED FROM THE SERVICE; OR (iv) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT.\n\nIN NO EVENT SHALL OUR AGGREGATE LIABILITY EXCEED THE GREATER OF ONE HUNDRED DOLLARS (USD $100) OR THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.",
      s11Title: "11. Termination",
      s11Body: "We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including without limitation if you breach these Terms.\n\nUpon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service or contact us at support@adambusinesshub.com.\n\nAll provisions of the Terms which by their nature should survive termination shall survive termination, including without limitation ownership provisions, warranty disclaimers, indemnity, and limitations of liability.\n\nUpon account termination, we will retain your data for a period of 30 days, after which it may be permanently deleted.",
      s12Title: "12. Governing Law",
      s12Body: "These Terms shall be governed and construed in accordance with the laws of Mexico, without regard to its conflict of law provisions. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in Mexico City, Mexico.\n\nIf you are accessing the Service from outside Mexico, you do so at your own risk and are responsible for compliance with local laws. We make no representation that the Service is appropriate or available in locations outside Mexico.\n\nThe parties agree that the United Nations Convention on Contracts for the International Sale of Goods does not apply to these Terms.",
      s13Title: "13. Changes to Terms",
      s13Body: "We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.\n\nBy continuing to access or use the Service after any revisions become effective, you agree to be bound by the revised Terms. If you do not agree to the new Terms, please stop using the Service.\n\nWe will indicate the date these Terms were last revised at the top of this page. We encourage you to review these Terms periodically for any changes.",
      s14Title: "14. Contact Information",
      s14Body: "If you have any questions about these Terms and Conditions, please contact us:\n\n[FULL_LEGAL_NAME] (persona física), operating as \"Innbuilder\"\nDomicilio fiscal: [FISCAL_ADDRESS], [CITY], [STATE], México · RFC [RFC]\nEmail: support@adambusinesshub.com\nWebsite: adambusinesshub.com\n\nFor billing inquiries: billing@adambusinesshub.com\nFor privacy matters: privacy@adambusinesshub.com\n\nWe will make every effort to respond to your inquiry within 5 business days.",
    },
  },
  calendar: {
    title: "Calendar",
    subtitle: "Sales, expenses, inventory requirements and events in one place",
    newEvent: "New Event",
    editEvent: "Edit Event",
    deleteEvent: "Delete Event",
    deleteConfirm: "Are you sure you want to delete \"{{title}}\"?",
    eventCreated: "Event created",
    eventUpdated: "Event updated",
    eventDeleted: "Event deleted",
    today: "Today",
    noItems: "Nothing scheduled this day",
    unassigned: "Unassigned",
    eventTitle: "Title",
    type: "Type",
    description: "Description",
    date: "Date",
    endDate: "End date",
    addEndDate: "Add end date",
    addTime: "Add time",
    removeTime: "Remove time",
    status: "Status",
    assignedTo: "Assigned to",
    kinds: {
      sale: "Sales",
      expense: "Expenses",
      inventory: "Inventory",
      event: "Events",
    },
    types: {
      appointment: "Appointment",
      reminder: "Reminder",
      task: "Task",
      other: "Other",
    },
    statuses: {
      pending: "Pending",
      in_progress: "In Progress",
      completed: "Completed",
      cancelled: "Cancelled",
    },
    inventoryStatuses: {
      low_stock: "Low stock",
      out_of_stock: "Out of stock",
    },
    allStatuses: "All",
    markComplete: "Mark complete",
    allDay: "All day",
    balanceDue: "Due {{amount}}",
    itemsCount_one: "{{count}} item",
    itemsCount_other: "{{count}} items",
    payLabel: "Pay",
    filterEvents: "Filter events:",
  },
  notifications: {
    title: "Notifications",
    subtitle: "Your activity and alerts",
    markAllRead: "Mark all as read",
    markRead: "Mark as read",
    noNotifications: "No notifications",
    noUnread: "You're all caught up!",
    viewAll: "View all notifications",
    viewRelated: "View related record",
    prefsSaved: "Preferences saved",
    allStatuses: "All statuses",
    allCategories: "All categories",
    total: "notifications",
    preferences: "Notification Preferences",
    managePreferences: "Manage preferences",
    emailEnabled: "Enable email notifications",
    emailEnabledHint: "Receive notifications via email when enabled per category",
    category: "Category",
    inApp: "In-app",
    email: "Email",
    statuses: {
      unread: "Unread",
      read: "Read",
      archived: "Archived",
    },
    categories: {
      goal_achieved: "Goal Achieved",
      expense_threshold: "Expense Threshold",
      payment_due: "Payment Due",
      payment_overdue: "Payment Overdue",
      appointment_upcoming: "Appointment Upcoming",
      reminder_upcoming: "Reminder Upcoming",
      calendar_change: "Calendar Change",
      sales_activity: "Sales Activity",
      deadline_proximity: "Deadline Proximity",
      system: "System",
    },
  },
  goals: {
    title: "Goals",
    subtitle: "Track progress toward your targets.",
    newGoal: "New goal",
    editGoal: "Edit goal",
    empty: "No goals yet.",
    createFirst: "Create your first goal",
    allStatuses: "All statuses",
    allModules: "All sections",
    any: "Any",
    notifications: "Notifications",
    daysRemaining_one: "{{count}} day remaining",
    daysRemaining_other: "{{count}} days remaining",
    windowClosed: "Window closed",
    confirmDelete: "Delete this goal?",
    reevaluate: "Re-evaluate",
    reevaluating: "Re-evaluating…",
    reevaluateTitle: "Recalculate progress for all active goals",
    reevaluatedToast_one: "Re-evaluated {{count}} goal.",
    reevaluatedToast_other: "Re-evaluated {{count}} goals.",
    atRisk: "At risk",
    filterStatus: "Status",
    filterModule: "Section",
    sortBy: "Sort by",
    emptyFiltered: "No goals match these filters.",
    clearFilters: "Clear filters",
    statuses: {
      active: "Active",
      achieved: "Achieved",
      missed: "Missed",
      archived: "Archived",
    },
    statusesDecrease: {
      active: "On track",
      achieved: "Within budget",
      missed: "Exceeded",
      archived: "Archived",
    },
    direction: {
      label: "Goal direction",
      increase: "Reach this target",
      increaseHint: "More is better — celebrate when you hit the target.",
      decrease: "Stay under this cap",
      decreaseHint: "Less is better — warn when you approach the cap, alert if you go over.",
    },
    targetLabel: {
      increase: "Target value",
      decrease: "Spending cap",
    },
    sections: {
      active: "Active",
      closed: "Past goals",
    },
    sort: {
      deadline: "Deadline",
      progress: "Progress",
    },
    kpi: {
      active: "Active",
      atRisk: "At risk",
      achieved: "Achieved",
      missed: "Missed",
    },
    modules: {
      sales: "Sales",
      customers: "Customers",
      expenses: "Expenses",
      inventory: "Inventory",
      calendar: "Calendar",
      catalog: "Catalog",
      employees: "Employees",
      suppliers: "Suppliers",
    },
    fields: {
      name: "Name",
      description: "Description",
      metric: "Metric",
      target: "Target value",
      start: "Start date",
      end: "End date",
    },
    placeholders: {
      name: "e.g. 10 new customers in April",
    },
    form: {
      sections: {
        measure: "What to measure",
        goal: "Goal",
        window: "Time window",
        notifications: "Notifications",
      },
    },
    notify: {
      achieved: "Notify when goal is achieved (100%)",
      milestones: "Notify at milestones (50%, 75%)",
      missed: "Notify if goal is missed at end date",
      digest: "Include in daily digest",
    },
    notifyDecrease: {
      achieved: "Notify if I stay under the cap at end of window",
      milestones: "Warn at 75% and 90% of the cap",
      missed: "Alert immediately if the cap is exceeded",
      digest: "Include in daily digest",
    },
  },
};

export default en;
export type Translation = typeof en;
